<template>
  <div class="settings-countries-section">
    <SettingsSectionHeader
      title="Countries"
      subtitle="Names used by work sites and employee profiles"
      icon="pi-globe"
    >
      <template #actions>
        <Button
          label="Add"
          icon="pi pi-plus"
          size="small"
          :disabled="mutating"
          @click="openCreate"
        />
        <Button
          icon="pi pi-refresh"
          label="Refresh"
          size="small"
          severity="secondary"
          outlined
          :loading="loading"
          @click="loadCountries"
        />
      </template>
    </SettingsSectionHeader>

    <div class="settings-countries-section__body">
      <div v-if="loading && !countries.length" class="settings-countries-section__state">
        <ProgressSpinner style="width: 2rem; height: 2rem" strokeWidth="4" />
      </div>

      <Message v-else-if="error" severity="error" :closable="false" class="settings-countries-section__message">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3">
          <span class="flex-1">{{ error }}</span>
          <Button label="Retry" size="small" severity="danger" outlined @click="loadCountries" />
        </div>
      </Message>

      <template v-else>
        <SettingsOrgReferenceToolbar
          v-model:search-query="searchQuery"
          :count-label="countLabel"
          :show-inactive-toggle="false"
          search-placeholder="Search countries..."
          :loading="loading"
        />

        <div v-if="!filteredCountries.length" class="settings-countries-section__empty">
          <i class="pi pi-globe settings-countries-section__empty-icon" aria-hidden="true" />
          <p>{{ emptyMessage }}</p>
          <Button
            v-if="searchQuery"
            label="Clear search"
            size="small"
            severity="secondary"
            text
            @click="searchQuery = ''"
          />
        </div>

        <DataTable
          v-else
          :value="filteredCountries"
          size="small"
          dataKey="id"
          sort-mode="single"
          removable-sort
          :sort-field="sortField"
          :sort-order="sortOrder"
          @sort="onSort"
        >
          <Column field="label" header="Country" sortable style="min-width: 12rem">
            <template #body="{ data }">
              <span class="font-medium">{{ data.label }}</span>
            </template>
          </Column>
          <Column field="locations_count" header="Work sites" sortable style="width: 7rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">{{ data.locations_count ?? 0 }}</span>
            </template>
          </Column>
          <Column field="employees_count" header="Employees" sortable style="width: 7rem">
            <template #body="{ data }">
              <span class="text-sm text-surface-600 dark:text-surface-400">{{ data.employees_count ?? 0 }}</span>
            </template>
          </Column>
          <Column header="Actions" style="width: 7rem">
            <template #body="{ data }">
              <div class="flex items-center gap-1">
                <Button
                  icon="pi pi-pencil"
                  size="small"
                  severity="secondary"
                  outlined
                  rounded
                  class="crc-table-action-btn"
                  v-tooltip.top="'Edit'"
                  aria-label="Edit"
                  :disabled="mutating"
                  @click="openEdit(data)"
                />
                <span
                  v-tooltip.top="isCountryInUse(data) ? 'Used by work sites or employee profiles.' : 'Delete'"
                >
                  <Button
                    icon="pi pi-trash"
                    size="small"
                    severity="danger"
                    outlined
                    rounded
                    class="crc-table-action-btn"
                    :disabled="mutating || isCountryInUse(data)"
                    @click="confirmDelete(data)"
                  />
                </span>
              </div>
            </template>
          </Column>
        </DataTable>
      </template>
    </div>

    <CountryEditModal
      ref="editModalRef"
      v-model:visible="editModalVisible"
      :saving="mutating"
      :save-error="saveError"
      @save="handleSave"
    />
  </div>
</template>

<script setup>
import { useSettingsDraftGuard } from '../../composables/useSettingsDraftGuard';
import { computed, onMounted, ref } from 'vue';
import { useToast } from 'primevue/usetoast';
import { useConfirm } from 'primevue/useconfirm';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import SettingsSectionHeader from '../SettingsSectionHeader.vue';
import SettingsOrgReferenceToolbar from '../SettingsOrgReferenceToolbar.vue';
import CountryEditModal from '../modals/CountryEditModal.vue';
import { countriesApi } from '@/api/countriesApi';
import { invalidateOrgReferenceOptions } from '@/api/orgReferenceApi';
import { replaceCountryCatalog } from '@/utils/countryCatalogState';
import { matchesSearchText } from '@/utils/searchText';
import { useSettingsLoadGuard } from '../../composables/useSettingsLoadGuard';
import { useClientTableSort } from '@/utils/tableSort';

defineOptions({ name: 'SettingsCountriesSection' });

defineProps({
  section: {
    type: Object,
    default: null,
  },
});

defineEmits(['close-settings']);

const toast = useToast();
const confirm = useConfirm();
const { sortField, sortOrder, onSort } = useClientTableSort('label');

const countries = ref([]);
const loading = ref(false);
const beginLoad = useSettingsLoadGuard();
const mutating = ref(false);
const saveError = ref(null);
const error = ref('');
const searchQuery = ref('');
const editModalVisible = ref(false);
const editModalRef = ref(null);

const countLabel = computed(() => {
  const total = countries.value.length;
  return total === 1 ? '1 country' : `${total} countries`;
});

const filteredCountries = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return countries.value;
  return countries.value.filter((row) => matchesSearchText([row.label, row.name], query));
});

const emptyMessage = computed(() => (
  searchQuery.value.trim() ? 'No countries match this search.' : 'No countries yet.'
));

const isCountryInUse = (country) => (country?.locations_count || 0) + (country?.employees_count || 0) > 0;

const notifyError = (err, fallback) => {
  toast.add({
    severity: 'error',
    summary: 'Could not save',
    detail: err?.message || fallback,
    life: 5000,
  });
};

const notifySuccess = (detail) => {
  toast.add({ severity: 'success', summary: 'Saved', detail, life: 2000 });
};

const loadCountries = async () => {
  const isCurrent = beginLoad();
  loading.value = true;
  error.value = '';
  try {
    const data = await countriesApi.list();
    if (!isCurrent()) return;
    const rows = Array.isArray(data?.countries) ? data.countries : [];
    countries.value = rows;
    replaceCountryCatalog(rows);
  } catch (err) {
    if (!isCurrent()) return;
    error.value = err?.message || 'Failed to load countries.';
    countries.value = [];
  } finally {
    if (isCurrent()) loading.value = false;
  }
};

const openCreate = () => {
  saveError.value = null;
  editModalRef.value?.openNew?.();
  editModalVisible.value = true;
};

const openEdit = (country) => {
  saveError.value = null;
  editModalRef.value?.openEdit?.(country);
  editModalVisible.value = true;
};

const handleSave = async ({ mode, payload }) => {
  if (mutating.value) return;
  saveError.value = null;
  mutating.value = true;
  try {
    if (mode === 'new') {
      await countriesApi.create(payload);
      notifySuccess('Country created');
    } else {
      await countriesApi.update(payload);
      notifySuccess('Country updated');
    }
    editModalVisible.value = false;
    invalidateOrgReferenceOptions();
    await loadCountries();
  } catch (err) {
    saveError.value = { message: err?.message || 'Failed to save changes.' };
    notifyError(err, mode === 'new' ? 'Failed to create country.' : 'Failed to update country.');
  } finally {
    mutating.value = false;
  }
};

const confirmDelete = (country) => {
  confirm.require({
    header: 'Delete country',
    message: `Delete "${country.label || country.name}"? This cannot be undone.`,
    icon: 'pi pi-exclamation-triangle',
    rejectLabel: 'Cancel',
    acceptLabel: 'Delete',
    acceptClass: 'p-button-danger',
    accept: () => deleteCountry(country),
  });
};

const deleteCountry = async (country) => {
  if (mutating.value) return;
  mutating.value = true;
  try {
    await countriesApi.delete({ country_id: country.id });
    notifySuccess('Country deleted');
    invalidateOrgReferenceOptions();
    await loadCountries();
  } catch (err) {
    notifyError(err, 'Failed to delete country.');
  } finally {
    mutating.value = false;
  }
};

onMounted(() => {
  loadCountries();
});
useSettingsDraftGuard({ draft: {}, saving: () => mutating.value });
</script>

<style scoped>
.settings-countries-section {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
}

.settings-countries-section__body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 1rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.settings-countries-section__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.settings-countries-section__message {
  margin: 0;
}

.settings-countries-section__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 12rem;
  text-align: center;
  color: var(--p-text-muted-color);
}

.settings-countries-section__empty-icon {
  font-size: 1.75rem;
}

.settings-countries-section__empty p {
  margin: 0;
}
</style>
